import { useState, type ReactNode } from 'react'
import { useI18n } from '../../i18n/I18nProvider'
import { cx } from '../ui/primitives'
import { AlertIcon, ChevronDownIcon, ShieldIcon } from '../ui/icons'
import { FlowDiagram } from './FlowDiagram'

function SectionHeading({
  id,
  title,
  subtitle,
}: {
  id: string
  title: string
  subtitle?: string
}) {
  return (
    <header className="mb-6">
      <h2
        id={id}
        className="text-2xl font-semibold tracking-tight text-balance text-fg sm:text-3xl"
      >
        {title}
      </h2>
      {subtitle && (
        <p className="mt-2 max-w-2xl text-sm text-pretty text-fg-muted sm:text-base">
          {subtitle}
        </p>
      )}
    </header>
  )
}

function Section({
  id,
  children,
  className,
}: {
  id: string
  children: ReactNode
  className?: string
}) {
  return (
    <section
      id={id}
      aria-labelledby={`${id}-titulo`}
      className={cx('scroll-mt-20 py-12 sm:py-16', className)}
    >
      {children}
    </section>
  )
}

// ---------------------------------------------------------------------------

export function HowItWorks() {
  const { t } = useI18n()
  return (
    <Section id="como-funciona">
      <SectionHeading id="como-funciona-titulo" title={t.how.title} subtitle={t.how.subtitle} />
      <ol className="mb-6 grid gap-4 sm:grid-cols-3">
        {t.how.steps.map((step, index) => (
          <li key={step.title} className="surface-card p-5">
            <span className="accent-gradient text-on-accent mb-3 inline-flex size-8 items-center justify-center rounded-full text-sm font-bold">
              {index + 1}
            </span>
            <h3 className="text-base font-semibold text-fg">{step.title}</h3>
            <p className="mt-1.5 text-sm leading-relaxed text-fg-muted">{step.body}</p>
          </li>
        ))}
      </ol>
      <FlowDiagram />
    </Section>
  )
}

// ---------------------------------------------------------------------------

export function Security() {
  const { t } = useI18n()
  return (
    <Section id="seguranca">
      <SectionHeading id="seguranca-titulo" title={t.sec.title} subtitle={t.sec.subtitle} />
      <div className="grid gap-6 lg:grid-cols-2">
        <ThreatColumn
          tone="ok"
          icon={<ShieldIcon className="size-5" />}
          title={t.sec.protectsTitle}
          items={t.sec.protects}
        />
        <ThreatColumn
          tone="warn"
          icon={<AlertIcon className="size-5" />}
          title={t.sec.limitsTitle}
          items={t.sec.limits}
        />
      </div>
    </Section>
  )
}

function ThreatColumn({
  tone,
  icon,
  title,
  items,
}: {
  tone: 'ok' | 'warn'
  icon: ReactNode
  title: string
  items: ReadonlyArray<{ title: string; body: string }>
}) {
  const accent = tone === 'ok' ? 'text-ok' : 'text-warn'
  const edge = tone === 'ok' ? 'border-l-ok' : 'border-l-warn'
  return (
    <div className="surface-card p-5 sm:p-6">
      <h3 className={cx('mb-4 flex items-center gap-2 text-sm font-semibold', accent)}>
        {icon}
        {title}
      </h3>
      <ul className="flex flex-col gap-4">
        {items.map((item) => (
          <li key={item.title} className={cx('border-l-2 pl-4', edge)}>
            <h4 className="text-sm font-semibold text-fg">{item.title}</h4>
            <p className="mt-1 text-sm leading-relaxed text-fg-muted">{item.body}</p>
          </li>
        ))}
      </ul>
    </div>
  )
}

// ---------------------------------------------------------------------------

export function Practice() {
  const { t } = useI18n()
  return (
    <Section id="na-pratica">
      <SectionHeading
        id="na-pratica-titulo"
        title={t.compat.title}
        subtitle={t.compat.subtitle}
      />
      <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {t.compat.tips.map((tip) => (
          <li key={tip.title} className="surface-card p-5">
            <h3 className="text-sm font-semibold text-fg">{tip.title}</h3>
            <p className="mt-1.5 text-sm leading-relaxed text-fg-muted">{tip.body}</p>
          </li>
        ))}
      </ul>
    </Section>
  )
}

// ---------------------------------------------------------------------------

export function Faq() {
  const { t } = useI18n()
  const [open, setOpen] = useState<number | null>(0)

  return (
    <Section id="faq">
      <SectionHeading id="faq-titulo" title={t.faq.title} />
      <div className="surface-card divide-y divide-[var(--color-border)] overflow-hidden">
        {t.faq.items.map((item, index) => {
          const expanded = open === index
          return (
            <div key={item.q}>
              <h3>
                <button
                  type="button"
                  aria-expanded={expanded}
                  aria-controls={`faq-${index}`}
                  onClick={() => setOpen(expanded ? null : index)}
                  className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left transition hover:bg-surface-2"
                >
                  <span className="text-sm font-medium text-pretty text-fg">{item.q}</span>
                  <ChevronDownIcon
                    className={cx(
                      'size-4 shrink-0 text-fg-subtle transition-transform',
                      expanded && 'rotate-180',
                    )}
                  />
                </button>
              </h3>
              {expanded && (
                <div id={`faq-${index}`} className="animate-rise px-5 pb-5">
                  <p className="max-w-3xl text-sm leading-relaxed text-pretty text-fg-muted">
                    {item.a}
                  </p>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </Section>
  )
}
